import { useState } from "react";
import { Search, FileText, Loader2 } from "lucide-react";
import { ObservationsBlock } from "./ObservationsBlock";
import type { EccRunSummary, EccObservations } from "@/api/types";

const EMPTY_OBSERVATIONS: EccObservations = { presets: [], custom: [] };

type Props = {
  parentRun: EccRunSummary;
  onDrilldown: (section: string, callUniqueids?: string[]) => Promise<void>;
  onSecondary: (focusText: string, observations: EccObservations) => Promise<void>;
};

export function FollowupPanel({ parentRun, onDrilldown, onSecondary }: Props) {
  const [drilldownSection, setDrilldownSection] = useState("");
  const [focusText, setFocusText] = useState("");
  const [secondaryObs, setSecondaryObs] = useState<EccObservations>(
    parentRun.observations ?? EMPTY_OBSERVATIONS,
  );
  const [loadingDrill, setLoadingDrill] = useState(false);
  const [loadingSecondary, setLoadingSecondary] = useState(false);
  const [drillError, setDrillError] = useState<string | null>(null);
  const [secondaryError, setSecondaryError] = useState<string | null>(null);

  const SECTIONS = ["H1", "H2", "H3", "H4", "H5", "H6"];

  async function handleDrilldown() {
    if (!drilldownSection) return;
    setDrillError(null);
    setLoadingDrill(true);
    try {
      await onDrilldown(drilldownSection);
    } catch {
      setDrillError("Error al iniciar la profundización.");
    } finally {
      setLoadingDrill(false);
    }
  }

  async function handleSecondary() {
    if (focusText.trim().length < 10) return;
    setSecondaryError(null);
    setLoadingSecondary(true);
    try {
      await onSecondary(focusText.trim(), secondaryObs);
    } catch {
      setSecondaryError("Error al iniciar el reporte secundario.");
    } finally {
      setLoadingSecondary(false);
    }
  }

  return (
    <div className="mt-6 space-y-3">
      <p className="text-sm font-medium text-muted-foreground">Análisis de seguimiento</p>
      <div className="grid grid-cols-2 gap-4">
        {/* Drilldown */}
        <div className="space-y-3 rounded-lg border bg-card p-4">
          <div className="flex items-center gap-2">
            <Search className="size-4 text-primary" />
            <span className="text-sm font-medium">Profundizar en…</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Genera un reporte enfocado en un hallazgo específico reutilizando las transcripciones existentes (~30s).
          </p>
          <select
            value={drilldownSection}
            onChange={(e) => setDrilldownSection(e.target.value)}
            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="">— Seleccionar hallazgo —</option>
            {SECTIONS.map((s) => (
              <option key={s} value={`hallazgos.${s}`}>
                {s}
              </option>
            ))}
          </select>
          {drillError && (
            <p className="text-xs text-destructive">{drillError}</p>
          )}
          <button
            type="button"
            disabled={!drilldownSection || loadingDrill}
            onClick={handleDrilldown}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {loadingDrill && <Loader2 className="size-3.5 animate-spin" />}
            Generar profundización
          </button>
        </div>

        {/* Secondary report */}
        <div className="space-y-3 rounded-lg border bg-card p-4">
          <div className="flex items-center gap-2">
            <FileText className="size-4 text-primary" />
            <span className="text-sm font-medium">Generar reporte secundario</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Análisis con foco libre. Describe el ángulo que quieres explorar (mín. 10 caracteres).
          </p>
          <textarea
            value={focusText}
            onChange={(e) => setFocusText(e.target.value)}
            placeholder="Ej: comparativo turno mañana vs tarde, agentes con menor puntaje en S3…"
            rows={3}
            maxLength={1000}
            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <ObservationsBlock
            presets={secondaryObs.presets}
            custom={secondaryObs.custom.map((c) => c.label)}
            onChange={setSecondaryObs}
          />
          {secondaryError && (
            <p className="text-xs text-destructive">{secondaryError}</p>
          )}
          <button
            type="button"
            disabled={focusText.trim().length < 10 || loadingSecondary}
            onClick={handleSecondary}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {loadingSecondary && <Loader2 className="size-3.5 animate-spin" />}
            Generar reporte secundario
          </button>
        </div>
      </div>
    </div>
  );
}
